<template>
  <div
    class="OperatorChat"
    :class="[isOpen ? 'Open' : 'Collapsed']"
  >
    <div
      class="Header flex items-center justify-between cursor-pointer px-4"
      @click="toggle"
    >
      <div
        class="text-base font-semibold w-full flex items-center gap-0.5"
      >
        <span>
          {{ title ? title : receiverName }}
        </span>

        <el-badge
          v-if="!chat.open && unreadMessages.length > 0"
          :value="unreadMessages.length"
          :max="99"
          type="danger"
          class="ml-1 flex"
        />
      </div>
      <div
        v-if="closeable"
        class="cursor-pointer ml-3"
        @click.stop="close"
      >
        <icon-close class="block w-3.5 h-auto" />
      </div>
    </div>
    <div
      v-show="isOpen"
      class="Body"
    >
      <ChatFeed
        :messages="chat.messages"
        :submit-disabled="submitDisabled"
        :slim="true"
        :is-visible="isOpen"
        @submit-message="submitMessage"
      />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    chat: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: undefined,
    },
    closeable: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    // Computed
    const documentVisible = useDocumentVisibility();
    const isOpen = computed(() => props.chat.open);
    const unreadMessages = computed(() => props.chat.messages.filter((message) =>
      message.id &&
      message.sender?.id !== useAuthStore().user.id &&
      message.read_by &&
      message.read_by?.every((readBy) => readBy?.id !== useAuthStore().user.id) &&
      !message.is_typing));

    // Methods
    const setMessageRead = () => {
      if (isOpen.value && unreadMessages.value.length && documentVisible.value === 'visible') {
        const lastMessage = _last(unreadMessages.value);

        useNuxtApp().$store.dispatch('chat/markMessageReadBy', {
          command: props.chat.room_id ? 'read_room_message' : 'read_private_message',
          roomId: props.chat.room_id,
          receiverId: lastMessage.sender?.id,
          messageId: lastMessage.id,
        });
      }
    };

    // Watchers
    watch([() => props.chat.messages, documentVisible, isOpen], () => {
      setMessageRead();
    }, { immediate: true });

    return {
      documentVisible,
      isOpen,
      unreadMessages,
    };
  },
  data() {
    return {
      sendingMessage: false,
    };
  },
  computed: {
    submitDisabled() {
      return this.sendingMessage || !this.wsConnected;
    },
    wsConnected() {
      return this.$store.state.websocket.isConnected;
    },
    operatorId() {
      return this.chat.receiver_id;
    },
    roomId() {
      return this.chat.room_id;
    },
    me() {
      return useAuthStore().user;
    },
    isOrganization() {
      if (!this.chat.receiver) { return false }
      return this.chat.receiver.type == 2;
    },
    receiverName() {
      if (!this.chat.receiver) { return '' }

      if (this.isOrganization) { return this.chat.receiver.name }

      if (this.chat.receiver.first_name && this.chat.receiver.last_name) { return `${this.chat.receiver.first_name} ${this.chat.receiver.last_name}` }

      if (this.chat.receiver.full_name) { return this.chat.receiver.full_name }

      return '';
    },
  },
  watch: {
    wsConnected: {
      immediate: true,
      handler(isConnected) {
        if (isConnected) {
          this.fetchMessages();
        }
      },
    },
  },
  methods: {
    close() {
      this.$store.dispatch('chat/closeChat', {
        receiver_id: this.operatorId,
        room_id: this.roomId,
      });
    },
    toggle(event) {
      if (event && event.target.closest('.icon')) {
        return;
      }

      if (this.chat.open) {
        this.$store.dispatch('chat/hideChat', {
          receiver_id: this.operatorId,
          room_id: this.roomId,
        });
      } else {
        this.$store.dispatch('chat/openChat', {
          room_id: this.roomId,
          receiver_id: this.operatorId,
          receiver: this.chat.receiver,
        });
      }
    },
    async submitMessage(text, { isTyping, isMessageByThirdParty }) {
      this.sendingMessage = true;

      const tempId = Math.random();
      const created_at = dayjs().format();

      const sendMessage = {
        content: text,
        created_at,
        temp_id: tempId,
        id: tempId,
        receiver_id: this.operatorId,
        room_id: this.roomId,
        is_typing: isTyping || false,
        third_party: isMessageByThirdParty || false,
        sender: this.me,
        status: 'pending',
        read_by: [],
      };

      await this.$store.dispatch('chat/sendMessage', sendMessage);

      this.sendingMessage = false;
    },
    fetchMessages() {
      const params = {
        receiver_id: this.operatorId,
        room_id: this.roomId,
      };

      this.$store.dispatch('chat/fetchMessages', params);
    },
  },
};
</script>

<style scoped>
@reference "assets/css/tailwind.css";

.OperatorChat {
  box-shadow: 0 25px 35px rgb(0 0 0 / 0.15);

  .Header {
    height: 36px;
    @apply bg-primary-500;
    @apply text-white;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  .Body {
    @apply bg-white;
  }

  &.Open {
    width: 364px;

    .Body {
      height: 420px;
      max-height: 60vh;
    }
  }

  &.Collapsed {
    max-width: 250px;
  }
}
</style>
